import { useState } from 'react'
import { Box, Typography, Collapse } from '@mui/material'
import { alpha } from '@mui/material/styles'
import { ExpandMoreRounded, ExpandLessRounded, CheckRounded } from '@mui/icons-material'
import { useSmartTranslation } from '../generic/hooks/useSmartTranslation'

export const STATUS_COLORS = {
  RAW: '#9e9e9e',
  WORKING: '#2196f3',
  REWORKED: '#ff9800',
  FINISHED: '#4caf50',
  DELIVERED: '#00bcd4',
  SCRAP: '#f44336',
  WORKFLOW_PAUSED: '#ffc107'
}

export default function StatusFilterChips ({ statuses, selected, onChange }) {
  const { t_ } = useSmartTranslation()
  const [open, setOpen] = useState(false)

  if (!statuses || statuses.length === 0) return null

  const activeCount = selected.length

  const toggle = (id) => {
    const next = selected.includes(id)
      ? selected.filter(s => s !== id)
      : [...selected, id]
    onChange(next)
  }

  return (
    <Box sx={{
      flexShrink: 0,
      borderRadius: 1.5,
      border: '1px solid rgba(255,255,255,0.07)',
      overflow: 'hidden'
    }}
    >
      {/* Collapsible header */}
      <Box
        onClick={() => setOpen(v => !v)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          cursor: 'pointer',
          px: 1.2,
          py: 0.8,
          minHeight: 52,
          userSelect: 'none',
          bgcolor: open ? 'rgba(255,255,255,0.04)' : 'transparent',
          '&:hover': { bgcolor: 'rgba(255,255,255,0.05)' }
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.7 }}>
          <Typography sx={{
            fontSize: '0.8rem',
            color: '#888',
            textTransform: 'uppercase',
            letterSpacing: '0.07em'
          }}
          >
            {t_('Status')}
          </Typography>
          {activeCount > 0 && (
            <Box sx={{
              bgcolor: 'rgba(127,225,167,0.15)',
              color: '#7FE1A7',
              border: '1px solid rgba(127,225,167,0.4)',
              borderRadius: 1,
              px: 0.6,
              fontSize: '0.62rem',
              fontWeight: 700,
              lineHeight: 1.6
            }}
            >
              {activeCount}
            </Box>
          )}
        </Box>
        {open
          ? <ExpandLessRounded sx={{ fontSize: 16, color: '#555' }} />
          : <ExpandMoreRounded sx={{ fontSize: 16, color: '#555' }} />}
      </Box>

      {/* Chips grid */}
      <Collapse in={open}>
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 0.6,
          px: 1,
          pt: 0.5,
          pb: 1,
          borderTop: '1px solid rgba(255,255,255,0.06)'
        }}
        >
          {statuses.map(s => {
            const id = s.id || s
            const color = STATUS_COLORS[id] || '#9e9e9e'
            const isOn = selected.includes(id)
            return (
              <Box
                key={id}
                onClick={() => toggle(id)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 0.5,
                  minHeight: 52,
                  borderRadius: 1.5,
                  cursor: 'pointer',
                  userSelect: 'none',
                  border: '1.5px solid',
                  borderColor: isOn ? color : alpha(color, 0.35),
                  bgcolor: isOn ? alpha(color, 0.18) : 'transparent',
                  transition: 'all 0.15s ease',
                  '&:hover': { bgcolor: alpha(color, isOn ? 0.25 : 0.08), borderColor: color }
                }}
              >
                {isOn && <CheckRounded sx={{ fontSize: 13, color, flexShrink: 0 }} />}
                <Typography sx={{
                  fontSize: '0.78rem',
                  fontWeight: isOn ? 700 : 500,
                  color: isOn ? color : alpha(color, 0.7),
                  letterSpacing: '0.02em',
                  textAlign: 'center',
                  lineHeight: 1.2
                }}
                >
                  {t_(s.name || id)}
                </Typography>
              </Box>
            )
          })}
        </Box>
      </Collapse>
    </Box>
  )
}
